import {
  Typography,
  Chip,
  Link,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { IconTrash, IconUser } from "@tabler/icons-react";
import { escalaQuantidade } from "../ui/inputs/quantidade";
import MoedaText from "../ui/inputs/moedaText";
import { map } from "lodash";
import InteiroText from "../ui/inputs/inteiroText";
import TabelaBase from "./tabelaBase";
import OperacaoInternacional from "../../../models/operacaoInternacional";
import {
  DebitoCredito,
  ROTULO_EVENTO_INTERNACIONAL,
  TipoOperacaoInternacional,
  ROTULO_LIQUIDACAO_OPCAO,
  LiquidacaoOpcao,
} from "../../../models/enum";
import moment from "moment-timezone";

// Rótulo e destino do statement de origem, por uploadId. Quando informado, a
// tabela ganha a coluna "Statement" com link — usado na visão mensal, onde as
// linhas vêm de arquivos diferentes.
export type StatementLink = { titulo: string; to: string };

type Props = {
  operacoes: OperacaoInternacional[];
  showDate?: boolean;
  statements?: { [uploadId: string]: StatementLink };
  // Quando informado, linhas registradas manualmente ganham a lixeira.
  onExcluirManual?: (operacao: OperacaoInternacional) => void;
};

// Tipo da linha do statement → rótulo PT + semântica de `palette.statusChip`.
// Compra sai de caixa (destructive), venda/dividendo/juros entram (positive),
// o resto é neutro. Mesma leitura do ChipOperacao nacional.
const TIPO_CHIP: Record<
  TipoOperacaoInternacional,
  {
    label: string;
    semantica: "destructive" | "positive" | "neutral" | "accent" | "warn";
  }
> = {
  [TipoOperacaoInternacional.BUY]: {
    label: "Compra",
    semantica: "destructive",
  },
  [TipoOperacaoInternacional.SELL]: { label: "Venda", semantica: "positive" },
  [TipoOperacaoInternacional.DIVIDEND]: {
    label: "Dividendo",
    semantica: "positive",
  },
  [TipoOperacaoInternacional.INTEREST]: {
    label: "Juros",
    semantica: "positive",
  },
  [TipoOperacaoInternacional.OTHER]: { label: "Outro", semantica: "neutral" },
  // Rótulo real vem de `evento` (Desdobramento/Grupamento); este é o fallback.
  [TipoOperacaoInternacional.CORPORATE_ACTION]: {
    label: "Evento",
    semantica: "accent",
  },
  // Imposto retido na fonte: saída de caixa, mas é dedução na DIRPF.
  [TipoOperacaoInternacional.WITHHOLDING_TAX]: {
    label: "Retenção",
    semantica: "warn",
  },
};

const ehEvento = (operacao: OperacaoInternacional) =>
  operacao.tipoOperacao === TipoOperacaoInternacional.CORPORATE_ACTION;

export default function TabelaOperacoesInternacionais({
  operacoes,
  showDate = false,
  statements,
  onExcluirManual,
}: Props) {
  // Descrição vive como sub-linha do Ativo e a moeda vai como prefixo do
  // valor ("USD 2.062,50", igual à custódia): duas colunas a menos, e o
  // símbolo "$" sozinho era ambíguo entre USD, CAD e AUD.
  const headerBase = [
    { titulo: "Data", key: "data", ordem: 1, align: "left" },
    { titulo: "Ativo", key: "ativo", ordem: 2, align: "left" },
    { titulo: "Tipo", key: "tipo", ordem: 3, align: "center" },
    { titulo: "Qtd", key: "quantidade", ordem: 4, align: "right" },
    { titulo: "Valor", key: "valor", ordem: 5, align: "right" },
    { titulo: "Valor (R$)", key: "valorBRL", ordem: 6, align: "right" },
    { titulo: "D/C", key: "debitoCredito", ordem: 7, align: "center" },
    { titulo: "Statement", key: "statement", ordem: 8, align: "left" },
    { titulo: "", key: "acoes", ordem: 9, align: "center" },
  ] as const;

  const header = headerBase.filter((column) => {
    if (column.key === "data" && !showDate) return false;
    if (column.key === "statement" && !statements) return false;
    if (column.key === "acoes" && !onExcluirManual) return false;
    return true;
  });

  const rows = map(operacoes, (operacao) => ({
    data: celulaData(operacao),
    ativo: celulaAtivo(operacao),
    tipo: celulaTipo(operacao),
    quantidade: celulaQuantidade(operacao),
    valor: celulaValor(operacao),
    valorBRL: celulaValorBRL(operacao),
    debitoCredito: celulaDebitoCredito(operacao),
    statement: celulaStatement(operacao),
    acoes: celulaAcoes(operacao),
  }));

  return <TabelaBase header={header} rows={rows} />;

  function celulaData(operacao: OperacaoInternacional): JSX.Element {
    return celulaMono(moment(operacao.dataPregao).format("DD/MM/YY"));
  }

  function celulaAtivo(operacao: OperacaoInternacional): JSX.Element {
    // Linha sem ativo (taxa, transferência) mostra só a descrição, em corpo.
    if (!operacao.ativo) {
      return (
        <Typography variant="body2" sx={{ maxWidth: 320 }}>
          {operacao.descricao}
        </Typography>
      );
    }
    // Ativo em mono + descrição do statement como sub-linha discreta
    // (DESIGN.md → Tabelas: sub-linha em ink-60).
    return (
      <Box sx={{ maxWidth: 320 }}>
        <Typography variant="dataMono" fontWeight={500} noWrap>
          {operacao.ativo}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", lineHeight: 1.3 }}
        >
          {operacao.descricao}
        </Typography>
      </Box>
    );
  }

  function celulaTipo(operacao: OperacaoInternacional): JSX.Element {
    const chip = operacao.tipoOperacao
      ? TIPO_CHIP[operacao.tipoOperacao]
      : undefined;
    if (!chip) return celulaMono("");
    const label =
      ehEvento(operacao) && operacao.evento
        ? ROTULO_EVENTO_INTERNACIONAL[operacao.evento]
        : chip.label;
    return (
      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
        <Chip
          label={label}
          size="small"
          sx={(theme) => ({
            backgroundColor: theme.palette.statusChip[chip.semantica].bg,
            color: theme.palette.statusChip[chip.semantica].fg,
          })}
        />
        {operacao.liquidacao && (
          <Tooltip
            title={
              operacao.liquidacao === LiquidacaoOpcao.EXERCICIO
                ? "Opção exercida/designada: a opção encerra sem resultado e o prêmio vai para o custo (ou a receita) da ação entregue no mesmo dia."
                : "Opção vencida: encerra a zero e realiza o prêmio inteiro."
            }
          >
            {/* Focável: o Tooltip só abre por teclado se o chip receber foco. */}
            <Chip
              label={ROTULO_LIQUIDACAO_OPCAO[operacao.liquidacao]}
              size="small"
              tabIndex={0}
              sx={(theme) => ({
                // O Chip do MUI zera o outline: foco visível explícito.
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
                backgroundColor: theme.palette.statusChip.neutral.bg,
                color: theme.palette.statusChip.neutral.fg,
              })}
            />
          </Tooltip>
        )}
        {operacao.duplicataDeUploadId && (
          <Tooltip title="Linha igual já importada de outro statement desta corretora. Não entra no cálculo enquanto aquele statement existir.">
            <Chip
              label="repetida"
              size="small"
              tabIndex={0}
              sx={(theme) => ({
                // O Chip do MUI zera o outline: foco visível explícito.
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
                backgroundColor: theme.palette.statusChip.warn.bg,
                color: theme.palette.statusChip.warn.fg,
              })}
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  function celulaQuantidade(operacao: OperacaoInternacional): JSX.Element {
    // Evento: mostra o multiplicador ("× 10") ou o divisor ("÷ 5").
    if (ehEvento(operacao)) {
      const fator = operacao.fator ?? 0;
      if (!(fator > 0)) return celulaMono("");
      const texto =
        fator >= 1
          ? `× ${fator.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}`
          : `÷ ${(1 / fator).toLocaleString("pt-BR", {
              maximumFractionDigits: 4,
            })}`;
      return celulaMono(texto);
    }
    if (operacao.quantidade === undefined) return celulaMono("");
    return (
      <Typography variant="dataMono">
        <InteiroText
          value={operacao.quantidade}
          decimalScale={escalaQuantidade(operacao.quantidade)}
          displayType={"text"}
        ></InteiroText>
      </Typography>
    );
  }

  function celulaValor(operacao: OperacaoInternacional): JSX.Element {
    if (ehEvento(operacao)) return celulaMono("");
    return (
      <Typography variant="dataMono" noWrap>
        <MoedaText
          displayType={"text"}
          value={operacao.valorOperacao}
          prefix={`${operacao.moeda} `}
        ></MoedaText>
      </Typography>
    );
  }

  function celulaValorBRL(operacao: OperacaoInternacional): JSX.Element {
    if (ehEvento(operacao) || operacao.valorOperacaoBRL === undefined)
      return celulaMono("");
    return (
      <Typography variant="dataMono" noWrap>
        <MoedaText
          displayType={"text"}
          value={operacao.valorOperacaoBRL}
        ></MoedaText>
      </Typography>
    );
  }

  function celulaDebitoCredito(operacao: OperacaoInternacional): JSX.Element {
    if (ehEvento(operacao)) return celulaMono("");
    // Apenas a letra, colorida conforme débito/crédito, sem badge
    const letra = operacao.debitoCredito === DebitoCredito.Debito ? "D" : "C";
    const cor =
      operacao.debitoCredito === DebitoCredito.Debito
        ? "error.main"
        : "success.main";
    return (
      <Typography
        variant="dataMono"
        sx={{ color: cor, fontWeight: 600, textAlign: "center" }}
      >
        {letra}
      </Typography>
    );
  }

  function celulaStatement(operacao: OperacaoInternacional): JSX.Element {
    if (operacao.manual) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <IconUser size={14} />
          Manual
        </Typography>
      );
    }
    const statement = statements?.[operacao.uploadId];
    if (!statement) return celulaMono("");
    return (
      <Link
        component={RouterLink}
        to={statement.to}
        variant="body2"
        underline="hover"
        noWrap
        sx={{ display: "inline-block", maxWidth: 200 }}
      >
        {statement.titulo}
      </Link>
    );
  }

  function celulaAcoes(operacao: OperacaoInternacional): JSX.Element {
    if (!onExcluirManual || !operacao.manual) return <></>;
    return (
      <Tooltip title="Excluir evento">
        <IconButton
          size="small"
          aria-label="Excluir evento"
          onClick={() => onExcluirManual(operacao)}
        >
          <IconTrash size={16} />
        </IconButton>
      </Tooltip>
    );
  }

  function celulaMono(texto?: string): JSX.Element {
    return <Typography variant="dataMono">{texto}</Typography>;
  }
}
